import { Component, OnDestroy, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Subscription as RxSub } from 'rxjs';
import { take } from 'rxjs/operators';
import { NewInternetService, NewInternetData } from './new-internet.service';
import {
  FeedItem, Subscription, Category, LikedItem, HistoryEntry,
} from './new-internet.model';

type Tab = 'stream' | 'library' | 'history' | 'feeds';

@Component({
    selector: 'app-new-internet',
    templateUrl: './new-internet.component.html',
    styleUrls: ['./new-internet.component.scss'],
    standalone: false
})
export class NewInternetComponent implements OnInit, OnDestroy {
  tab: Tab = 'stream';
  search = '';

  uid = '';
  loading = true;
  streamLoading = false;

  subscriptions: Subscription[] = [];
  categories: Category[] = [];
  likes: LikedItem[] = [];
  history: HistoryEntry[] = [];
  readHashes = new Set<string>();

  feedItems: FeedItem[] = [];
  feedErrors: { [feedUrl: string]: string } = {};

  // category picker overlay state
  pickerLike: LikedItem | null = null;
  newCategoryName = '';

  private dataSub?: RxSub;
  private lastStreamKey = '';

  constructor(private afAuth: AngularFireAuth, private svc: NewInternetService) {}

  ngOnInit(): void {
    this.afAuth.authState.pipe(take(1)).subscribe(user => {
      if (!user) { return; }
      this.uid = user.uid;
      this.dataSub = this.svc.subscribeAll(this.uid).subscribe(data => this.applyData(data));
    });
  }

  ngOnDestroy(): void {
    this.dataSub?.unsubscribe();
  }

  private applyData(data: NewInternetData): void {
    this.subscriptions = data.subscriptions;
    this.categories = data.categories;
    this.likes = data.likes;
    this.history = data.history;
    this.readHashes = data.readHashes;
    this.loading = false;
    // Re-fetch the stream only when the set of feeds actually changes.
    const key = data.subscriptions.map(s => s.feedUrl).sort().join('|');
    if (key !== this.lastStreamKey) {
      this.lastStreamKey = key;
      this.reloadStream();
    } else {
      this.decorateItems();
    }
  }

  async reloadStream(): Promise<void> {
    if (this.subscriptions.length === 0) {
      this.feedItems = [];
      this.feedErrors = {};
      return;
    }
    this.streamLoading = true;
    const likedHashes = new Set(this.likes.map(l => l.itemHash));
    const result = await this.svc.loadStream(this.subscriptions, this.readHashes, likedHashes);
    this.feedItems = result.items;
    this.feedErrors = result.errors;
    this.streamLoading = false;
  }

  // Re-apply read/liked flags to already-loaded items without re-fetching.
  private decorateItems(): void {
    const likedHashes = new Set(this.likes.map(l => l.itemHash));
    this.feedItems = this.feedItems.map(it => ({
      ...it,
      read: this.readHashes.has(it.itemHash),
      liked: likedHashes.has(it.itemHash),
    }));
  }

  get unreadCount(): number {
    return this.feedItems.filter(i => !i.read).length;
  }

  get unreadByFeed(): { [feedUrl: string]: number } {
    const out: { [feedUrl: string]: number } = {};
    for (const it of this.feedItems) {
      if (!it.read) { out[it.feedUrl] = (out[it.feedUrl] || 0) + 1; }
    }
    return out;
  }

  setTab(tab: Tab): void {
    this.tab = tab;
  }

  // ---- item interactions ----
  onOpenItem(item: FeedItem): void {
    if (!item.read) { this.svc.markRead(this.uid, item.itemHash); }
    this.svc.addHistory(this.uid, {
      itemHash: item.itemHash, title: item.title, link: item.link,
      source: item.source, publishedAt: item.publishedAt, viewedAt: Date.now(),
    });
  }

  onOpenLike(like: LikedItem): void {
    if (!this.readHashes.has(like.itemHash)) { this.svc.markRead(this.uid, like.itemHash); }
    this.svc.addHistory(this.uid, {
      itemHash: like.itemHash, title: like.title, link: like.link,
      source: like.source, publishedAt: like.publishedAt, viewedAt: Date.now(),
    });
  }

  async onToggleLike(item: FeedItem): Promise<void> {
    const existing = this.likes.find(l => l.itemHash === item.itemHash);
    if (existing) {
      await this.svc.removeLike(this.uid, existing.id);
      return;
    }
    const like: Omit<LikedItem, 'id'> = {
      itemHash: item.itemHash, title: item.title, link: item.link,
      source: item.source, sourceType: item.sourceType, author: item.author,
      publishedAt: item.publishedAt, summary: item.summary, thumbnail: item.thumbnail,
      category: null, likedAt: Date.now(),
    };
    const id = await this.svc.addLike(this.uid, like);
    this.pickerLike = { id, ...like };   // immediately open the category picker
  }

  onUnlike(like: LikedItem): void {
    this.svc.removeLike(this.uid, like.id);
  }

  openPicker(like: LikedItem): void {
    this.pickerLike = like;
  }

  chooseExisting(name: string): void {
    if (!this.pickerLike) { return; }
    this.svc.setLikeCategory(this.uid, this.pickerLike.id, name);
    this.closePicker();
  }

  createAndChoose(): void {
    const name = this.newCategoryName.trim();
    if (!name || !this.pickerLike) { return; }
    if (!this.categories.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      this.svc.addCategory(this.uid, name);
    }
    this.svc.setLikeCategory(this.uid, this.pickerLike.id, name);
    this.closePicker();
  }

  closePicker(): void {
    this.pickerLike = null;
    this.newCategoryName = '';
  }

  // ---- subscription interactions ----
  onAddSubscription(sub: Omit<Subscription, 'id'>): void {
    this.svc.addSubscription(this.uid, sub);
  }

  onRemoveSubscription(id: string): void {
    this.svc.removeSubscription(this.uid, id);
  }
}
